import "./styles.css";
import Phaser from "phaser";
import { AudioManager } from "./audio/AudioManager";
import {
  loadContent,
  type OperationalConfig,
} from "./game/content/load-content";
import type {
  ActiveEventSnapshot,
  GameState,
  LevelDefinition,
  LevelId,
  SessionSummary,
} from "./game/domain/types";
import { accuracyFor } from "./game/domain/game-state";
import { TypedGameBus } from "./game/events/typed-bus";
import { MobileInputController } from "./game/input/MobileInputController";
import { GameplayControlController } from "./game/input/GameplayControlController";
import { GameScene } from "./game/scenes/GameScene";
import { createGame } from "./game/create-game";

const $ = <T extends HTMLElement>(selector: string): T => {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Missing element ${selector}`);
  return element;
};

const LEVEL_PRESENTATION: Record<LevelId, { number: string; image: string }> = {
  vida: { number: "01", image: "./assets/vida.svg" },
  soat: { number: "02", image: "./assets/soat.svg" },
  rentas: { number: "03", image: "./assets/rentas.svg" },
};

const COMING_SOON_LEVEL = {
  number: "04",
  image: "./assets/viaje.svg",
  universe: "VIAJE",
  title: "Viaje seguro",
} as const;

class AppController {
  private readonly bus = new TypedGameBus();
  private readonly audio = new AudioManager();
  private readonly game: Phaser.Game;
  private readonly resizeObserver: ResizeObserver;
  private readonly attemptCounts: Record<LevelId, number> = {
    vida: 0,
    soat: 0,
    rentas: 0,
  };
  private levels!: Record<LevelId, LevelDefinition>;
  private operational!: OperationalConfig;
  private selectedLevelId: LevelId | null = null;
  private mobileInput: MobileInputController | null = null;
  private feedbackTimer = 0;
  private criticalUntil = 0;
  private activeScreen = "loading";
  private pendingLifecycleResume = false;
  private lifecycleResumeInFlight = false;

  constructor() {
    this.buildKeyboard();
    this.game = createGame($("#playfield"));
    this.resizeObserver = new ResizeObserver(() => this.syncGameSize());
    this.resizeObserver.observe($("#playfield"));
    this.bindStaticControls();
    this.bindBus();
    this.bindLifecyclePauses();
    new GameplayControlController({
      isGameplay: () => this.activeScreen === "playing",
      isPaused: () => !$("#pause-overlay").hidden,
      pause: () => this.pause(),
      resume: () => this.resume(),
    });
  }

  async bootstrap(): Promise<void> {
    this.showScreen("loading");
    try {
      const content = await loadContent();
      this.levels = content.levels;
      this.operational = content.operational;
      this.renderGoals();
      this.showScreen("start");
      this.installDebugHook();
    } catch (error) {
      console.error("Content validation failed", error);
      this.showScreen("error");
    }
  }

  private bindStaticControls(): void {
    $("#start-button").addEventListener("click", async () => {
      await this.audio.unlock();
      this.showScreen("goals");
    });
    $("#briefing-back").addEventListener("click", () =>
      this.showScreen("goals"),
    );
    $("#go-button").addEventListener("click", () => this.startSelectedLevel());
    $("#replay-button").addEventListener("click", () =>
      this.startSelectedLevel(),
    );
    $("#change-goal-button").addEventListener("click", () => {
      this.stopSession();
      this.showScreen("goals");
      this.audio.playMenu();
    });
    $("#pause-button").addEventListener("click", () => this.pause());
    $("#continue-button").addEventListener("click", () => this.resume());
    $("#retry-button").addEventListener("click", () => void this.bootstrap());
    $("#sound-toggle").addEventListener("click", () => {
      const willMute = !this.audio.isMuted();
      this.audio.setMuted(willMute);
      if (willMute) {
        this.pendingLifecycleResume = false;
        void this.audio.setLifecycleSuspended(false);
      } else if (this.pendingLifecycleResume) {
        void this.resumeLifecycleAudio();
      }
      this.updateSoundButton();
    });
    $("#cta-button").addEventListener("click", () => {
      this.pendingLifecycleResume = false;
      this.audio.setMuted(true);
      void this.audio.setLifecycleSuspended(false);
      this.updateSoundButton();
    });
  }

  private bindBus(): void {
    this.bus.on("state", ({ state, active }) => {
      this.renderHud(state);
      if (this.activeScreen === "playing") this.audio.syncGameplayState(state);
      this.mobileInput?.setEnabled(
        state.status === "playing" && active.length > 0,
      );
      const target = active.find(
        ({ instanceId }) => instanceId === state.targetInstanceId,
      );
      const description = target
        ? `Objetivo ${target.word}. ${Math.round((target.typedLength / target.word.length) * 100)} por ciento escrito.`
        : `${active.length} eventos activos. Escribe una inicial para seleccionar el objetivo más cercano.`;
      $("#canvas-status").textContent = description;
      $("#playfield").setAttribute("aria-label", description);
    });
    this.bus.on("audio", ({ cue, progress }) => this.audio.play(cue, progress));
    this.bus.on("feedback", ({ product, message }) =>
      this.showFeedback(product, message),
    );
    this.bus.on("visual", ({ cue, critical }) =>
      this.playUiVisual(cue, critical ?? false),
    );
    this.bus.on("ended", (summary) => this.showResults(summary));
  }

  private bindLifecyclePauses(): void {
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) this.suspendForLifecycle();
    });
    window.addEventListener("pagehide", () => this.suspendForLifecycle());
    window.addEventListener(
      "pointerdown",
      (event) => this.resumePendingAudioFromInteraction(event),
      { capture: true },
    );
    window.addEventListener(
      "keydown",
      (event) => this.resumePendingAudioFromInteraction(event),
      { capture: true },
    );
    window.addEventListener("blur", () => this.pause());
    window.addEventListener("orientationchange", () => this.pause());
  }

  private suspendForLifecycle(): void {
    this.pendingLifecycleResume = true;
    void this.audio.setLifecycleSuspended(true);
    this.pause();
  }

  private resumePendingAudioFromInteraction(
    event: PointerEvent | KeyboardEvent,
  ): void {
    if (!this.pendingLifecycleResume || document.hidden || !event.isTrusted)
      return;
    if (this.activeScreen === "playing" && $("#pause-overlay").hidden === false)
      return;
    if (event instanceof PointerEvent) {
      if (!event.isPrimary || event.button !== 0) return;
    } else if (
      event.repeat ||
      event.metaKey ||
      event.ctrlKey ||
      event.altKey ||
      ["Alt", "Control", "Meta", "Shift"].includes(event.key)
    ) {
      return;
    }
    const target = event.target;
    if (
      target instanceof Element &&
      target.closest("#cta-button, #sound-toggle")
    )
      return;
    void this.resumeLifecycleAudio();
  }

  private async resumeLifecycleAudio(): Promise<void> {
    if (!this.pendingLifecycleResume || this.lifecycleResumeInFlight) return;
    this.lifecycleResumeInFlight = true;
    const resumed = await this.audio.setLifecycleSuspended(false);
    this.lifecycleResumeInFlight = false;
    this.pendingLifecycleResume = !resumed;
  }

  private renderGoals(): void {
    const grid = $("#goal-grid");
    grid.replaceChildren();
    (["vida", "soat", "rentas"] as const).forEach((id) => {
      const level = this.levels[id];
      const button = document.createElement("button");
      button.type = "button";
      button.className = `goal-card goal-card--${id}`;
      button.dataset.level = id;
      button.innerHTML = `<span class="goal-card__number">${LEVEL_PRESENTATION[id].number}</span><span class="goal-card__motif" aria-hidden="true"><img src="${LEVEL_PRESENTATION[id].image}" alt="" /></span><small>${level.universe}</small><strong>${level.title}</strong><span>${level.objective}</span><span class="goal-card__action">Elegir esta meta →</span>`;
      button.addEventListener("click", () => this.openBriefing(id));
      grid.append(button);
    });
    const comingSoon = document.createElement("button");
    comingSoon.type = "button";
    comingSoon.disabled = true;
    comingSoon.className = "goal-card goal-card--viaje goal-card--locked";
    comingSoon.innerHTML = `<span class="goal-card__number">${COMING_SOON_LEVEL.number}</span><span class="goal-card__motif" aria-hidden="true"><img src="${COMING_SOON_LEVEL.image}" alt="" /></span><small>${COMING_SOON_LEVEL.universe}</small><strong>${COMING_SOON_LEVEL.title}</strong><span>Una nueva meta estará disponible pronto.</span><span class="goal-card__status"><span aria-hidden="true">🔒</span><span>PRÓXIMAMENTE</span></span>`;
    grid.append(comingSoon);
  }

  private openBriefing(id: LevelId): void {
    this.selectedLevelId = id;
    const level = this.levels[id];
    $("#briefing-universe").textContent = level.universe;
    $("#briefing-title").textContent = level.title;
    $("#briefing-objective").textContent = level.objective;
    $("#briefing-illustration").className =
      `briefing-illustration briefing-illustration--${id}`;
    $("#briefing-input").textContent = this.isCoarsePointer()
      ? "Usa el teclado del juego: la primera letra elige el evento más cercano. Los espacios avanzan solos."
      : "Empieza a escribir: la primera letra elige el evento más cercano. Los espacios avanzan solos.";
    this.showScreen("briefing");
    $("#go-button").focus();
  }

  private startSelectedLevel(): void {
    if (!this.selectedLevelId) return;
    const level = this.levels[this.selectedLevelId];
    const coarsePointer = this.isCoarsePointer();
    this.stopSession();
    this.showScreen("playing");
    this.syncGameSize();
    $("#hud-level").textContent = level.universe;
    const keyboard = $("#mobile-keyboard");
    keyboard.toggleAttribute("hidden", !coarsePointer);
    this.mobileInput = coarsePointer
      ? new MobileInputController(keyboard, (intent) =>
          this.currentScene()?.queueIntent(intent),
        )
      : null;
    const attemptIndex = this.attemptCounts[level.id];
    this.attemptCounts[level.id] += 1;
    const seed = new Uint32Array(1);
    crypto.getRandomValues(seed);
    this.game.scene.start("GameScene", {
      level,
      coarsePointer,
      attemptIndex,
      randomSeed: seed[0] ?? 0,
      bus: this.bus,
    });
    this.audio.playGame();
    this.installDebugHook();
  }

  private stopSession(): void {
    this.mobileInput?.dispose();
    this.mobileInput = null;
    if (
      this.game.scene.isActive("GameScene") ||
      this.game.scene.isPaused("GameScene")
    ) {
      this.game.scene.stop("GameScene");
    }
  }

  private pause(): void {
    if (this.activeScreen !== "playing" || $("#pause-overlay").hidden === false)
      return;
    const scene = this.currentScene();
    if (!scene || scene.getStateSnapshot().status !== "playing") return;
    scene.pauseSession();
    this.audio.setPaused(true);
    $("#pause-overlay").hidden = false;
    $("#continue-button").focus();
  }

  private resume(): void {
    const scene = this.currentScene();
    if (!scene) return;
    $("#pause-overlay").hidden = true;
    scene.resumeSession();
    this.audio.setPaused(false);
    if (this.pendingLifecycleResume) void this.resumeLifecycleAudio();
    $("#playfield").focus({ preventScroll: true });
  }

  private renderHud(state: GameState): void {
    $("#hud-hearts").textContent =
      `${"♥ ".repeat(state.hearts)}${"♡ ".repeat(3 - state.hearts)}`.trim();
    $("#hud-shield").textContent = state.shieldActive ? "ACTIVO" : "—";
    $("#hud-shield").classList.toggle("is-active", state.shieldActive);
    $("#hud-score").textContent = state.score.toLocaleString("es-PE");
    $("#hud-streak").textContent = `${state.streak} · x${state.multiplier}`;
    $("#hud-progress-text").textContent = `${state.processedCount}/20`;
    $("#hud-progress-bar").style.width =
      `${(state.processedCount / 20) * 100}%`;
    const track = $(".progress-track");
    track.setAttribute("aria-valuenow", String(state.processedCount));
  }

  private showFeedback(product: string, message: string): void {
    const now = performance.now();
    if (now < this.criticalUntil) {
      window.setTimeout(
        () => this.showFeedback(product, message),
        this.criticalUntil - now,
      );
      return;
    }
    window.clearTimeout(this.feedbackTimer);
    const feedback = $("#feedback");
    feedback.classList.remove("feedback--visible");
    window.setTimeout(() => {
      $("#feedback-product").textContent = `${product} · `;
      $("#feedback-message").textContent = message;
      feedback.hidden = false;
      feedback.classList.add("feedback--visible");
      this.feedbackTimer = window.setTimeout(() => {
        feedback.classList.remove("feedback--visible");
        window.setTimeout(() => (feedback.hidden = true), 100);
      }, 1200);
    }, 100);
  }

  private playUiVisual(cue: string, critical: boolean): void {
    const playfield = $(".playfield-wrap");
    playfield.dataset.cue = cue;
    playfield.classList.remove("is-pulsing", "is-shaking");
    void playfield.offsetWidth;
    playfield.classList.add(
      critical || cue === "typo" ? "is-shaking" : "is-pulsing",
    );
    if (critical) this.criticalUntil = performance.now() + 800;
    window.setTimeout(
      () => playfield.classList.remove("is-pulsing", "is-shaking"),
      critical ? 800 : 280,
    );
  }

  private showResults(summary: SessionSummary): void {
    const level = this.levels[summary.state.levelId];
    const won = summary.result === "victory";
    this.showScreen("results");
    const resultImage = $("#result-level-image") as HTMLImageElement;
    resultImage.src = LEVEL_PRESENTATION[summary.state.levelId].image;
    $("#result-universe").textContent = level.universe;
    $("#result-title").textContent = won
      ? "¡AVANZASTE SEGURO!"
      : "TU CAMINO CONTINÚA";
    $("#result-subtitle").textContent = won
      ? "Completaste tu recorrido y llegaste a la meta."
      : "Cada intento abre una nueva oportunidad para acercarte a tu meta.";
    $("#result-score").textContent =
      summary.state.score.toLocaleString("es-PE");
    $("#result-accuracy").textContent = `${accuracyFor(summary.state)}%`;
    $("#result-streak").textContent = String(summary.state.maxStreak);
    $("#result-multiplier").textContent = `x${summary.state.maxMultiplier}`;
    $("#result-resolved").textContent = `${summary.state.resolvedCount}/20`;
    $("#result-processed").textContent = `${summary.state.processedCount}/20`;
    const list = $("#discoveries-list");
    list.replaceChildren();
    if (summary.discoveries.length === 0) {
      const item = document.createElement("li");
      item.textContent = "Siempre puedes seguir avanzando.";
      list.append(item);
    } else {
      summary.discoveries.forEach(({ product, message }) => {
        const item = document.createElement("li");
        item.textContent = `${product} · ${message}`;
        list.append(item);
      });
    }
    const cta = $("#cta-button") as HTMLAnchorElement;
    cta.href = level.cta.url;
    cta.hidden = !this.operational.ctaEnabled;
    this.audio.showResult(won);
    window.scrollTo(0, 0);
    $("#result-title").focus({ preventScroll: true });
  }

  private showScreen(name: string): void {
    this.activeScreen = name;
    document.body.dataset.screen = name;
    document.querySelectorAll<HTMLElement>(".screen").forEach((screen) => {
      screen.hidden = screen.id !== `screen-${name}`;
    });
    $("#sound-toggle").hidden = name === "loading" || name === "error";
  }

  private updateSoundButton(): void {
    const button = $("#sound-toggle");
    const muted = this.audio.isMuted();
    button.setAttribute("aria-pressed", String(muted));
    button.setAttribute(
      "aria-label",
      muted ? "Activar sonido" : "Silenciar sonido",
    );
    button.querySelector("span")!.textContent = muted ? "🔇" : "🔊";
  }

  private buildKeyboard(): void {
    document.querySelectorAll<HTMLElement>(".keyboard-row").forEach((row) => {
      const letters = [...(row.dataset.row ?? "")];
      letters.forEach((letter) => {
        const key = document.createElement("button");
        key.type = "button";
        key.className = "keyboard-key";
        key.dataset.key = letter;
        key.textContent = letter;
        key.disabled = true;
        key.setAttribute("aria-label", `Letra ${letter}`);
        row.append(key);
      });
    });
  }

  private currentScene(): GameScene | null {
    try {
      return this.game.scene.getScene("GameScene") as GameScene;
    } catch {
      return null;
    }
  }

  private isCoarsePointer(): boolean {
    return (
      window.matchMedia("(pointer: coarse)").matches || window.innerWidth < 800
    );
  }

  private syncGameSize(): void {
    const playfield = $("#playfield");
    const width = Math.round(playfield.clientWidth);
    const height = Math.round(playfield.clientHeight);
    if (width > 0 && height > 0) this.game.scale.resize(width, height);
  }

  private installDebugHook(): void {
    if (!import.meta.env.DEV) return;
    window.__GAME_DEBUG__ = {
      resolveFirst: () => this.currentScene()?.debugResolveFirst() ?? false,
      expireFirst: () => this.currentScene()?.debugExpireFirst() ?? false,
      getState: () => this.currentScene()?.getStateSnapshot() ?? null,
      getActive: () => this.currentScene()?.getActiveSnapshots() ?? [],
      getAudio: () => this.audio.getDebugState(),
      setResources: (hearts, shieldActive = false) =>
        this.currentScene()?.debugSetResources(hearts, shieldActive),
    };
  }
}

declare global {
  interface Window {
    webkitAudioContext?: typeof AudioContext;
    __GAME_DEBUG__?: {
      resolveFirst: () => boolean;
      expireFirst: () => boolean;
      getState: () => GameState | null;
      getActive: () => ActiveEventSnapshot[];
      getAudio: () => ReturnType<AudioManager["getDebugState"]>;
      setResources: (hearts: 1 | 2 | 3, shieldActive?: boolean) => void;
    };
  }
}

const app = new AppController();
void app.bootstrap();
